import React from "react";
import { ArrowRight, Target, Wrench, MessageSquare, Award } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { PageHero, Section, PrimaryButton, SecondaryButton } from "@/components/ui-helpers";

const PRINCIPLES = [
  { icon: Target, t: "Detail-obsessed", d: "We focus on the areas most people overlook — tracks, hinges, hidden edges and crevices." },
  { icon: Wrench, t: "Properly equipped", d: "Professional wet/dry vacuum systems, steam equipment and surface-appropriate products." },
  { icon: MessageSquare, t: "Honest communication", d: "Clear scopes, clear quotes, and a straight answer when something needs a separate assessment." },
  { icon: Award, t: "Continuous improvement", d: "We build rigorous SOPs and quality standards, refining our process on every project." },
];

export default function About() {
  return (
    <>
      <SEO title="About Us" description="Ascendia is a growing, owner-led Israeli property-detailing company focused on meticulous execution and honest communication." />
      <PageHero kicker="About Ascendia" title="A property should feel properly prepared — in every detail" subtitle="Ascendia was built around one simple belief: a property should not merely look clean at first glance. It should feel properly prepared in every detail." image="https://images.unsplash.com/photo-1586323715429-7d4f8c5d05fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" />

      {/* Editorial manifesto — paper interlude */}
      <section className="paper-section section-y">
        <div className="container-ascendia">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
            <Reveal className="lg:col-span-5">
              <p className="kicker mb-4">Our belief</p>
              <p className="editorial-index" style={{ WebkitTextStroke: "1px rgba(200,161,74,0.5)" }}>01</p>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7">
              <p className="display-3" style={{ color: "var(--paper-ink)" }}>
                We don't just clean. We detail — treating every property with the care and precision it deserves, from construction site to move-in ready.
              </p>
              <p className="mt-6 u-body max-w-[var(--measure)]" style={{ color: "var(--paper-muted)" }}>
                Ascendia is a growing Israeli property-detailing company — owner-led, focused on continuous improvement, and investing in professional equipment and rigorous quality standards. We serve homeowners and property professionals who want more than a surface-level clean.
              </p>
              <p className="mt-4 u-body max-w-[var(--measure)]" style={{ color: "var(--paper-muted)" }}>
                Every job finishes with a documented final quality inspection — because the details most people overlook are exactly the details we focus on.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Principles */}
      <Section>
        <Reveal>
          <p className="kicker mb-3">What guides us</p>
          <h2 className="display-2 text-white max-w-2xl">Four principles behind every visit</h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.t} delay={(i % 2) * 0.08}>
              <div className="ascendia-card card-accent-top p-7 h-full">
                <div className="flex items-center justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/10 border border-[color:var(--border-subtle)]"><p.icon className="h-5 w-5 text-gold-soft" strokeWidth={1.5} /></div>
                  <span className="editorial-index select-none" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold text-white u-tracking-tight">{p.t}</h3>
                <p className="mt-2 text-sm text-secondary u-body">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section className="bg-[#0e0e0f] border-t border-[color:var(--border-subtle)] noise-overlay">
        <div className="text-center">
          <div className="gold-hairline max-w-xs mx-auto mb-8" />
          <h2 className="display-2 text-white">Ready for the Ascendia standard?</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <PrimaryButton to="/contact">Request a Quote <ArrowRight className="h-4 w-4" /></PrimaryButton>
            <SecondaryButton to="/ascendia-standard">See our method</SecondaryButton>
          </div>
        </div>
      </Section>
    </>
  );
}
